import type { Editor } from "@tiptap/react";
import { BubbleMenu } from "@tiptap/react/menus";
import {
  Bold,
  Code,
  Heading1,
  Heading2,
  Heading3,
  Italic,
  Link as LinkIcon,
  Quote,
  Strikethrough,
} from "lucide-react";
import { useCallback } from "react";

interface FormattingBubbleMenuProps {
  editor: Editor;
}

function BubbleMenuButton({
  onClick,
  isActive,
  icon: Icon,
  title,
  shortcut,
}: {
  onClick: () => void;
  isActive: boolean;
  icon: React.ReactNode;
  title: string;
  shortcut?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={shortcut ? `${title} (${shortcut})` : title}
      className={`px-2 py-1.5 rounded transition-all duration-150 ${
        isActive ? "bg-blue-600 text-white" : "text-gray-300 hover:bg-gray-700 hover:text-white"
      }`}
    >
      {Icon}
    </button>
  );
}

export function FormattingBubbleMenu({ editor }: FormattingBubbleMenuProps) {
  const handleLink = useCallback(() => {
    const previousUrl = editor.getAttributes("link").href;
    const url = window.prompt("URL", previousUrl);

    if (url === null) {
      return;
    }

    if (url === "") {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
      return;
    }

    let finalUrl = url;
    if (!url.startsWith("http://") && !url.startsWith("https://")) {
      finalUrl = "https://" + url;
    }

    editor.chain().focus().extendMarkRange("link").setLink({ href: finalUrl }).run();
  }, [editor]);

  return (
    <BubbleMenu
      editor={editor}
      shouldShow={({ editor, state }) => {
        // Show bubble menu when:
        // 1. There's a selection (not empty)
        // 2. It's not just a single NodeSelection (empty selection on a node)
        // 3. Editor is not in a code block
        const { from, to } = state.selection;
        const isEmpty = from === to;

        if (isEmpty || editor.isActive("codeBlock")) {
          return false;
        }

        return true;
      }}
    >
      <div className="bg-gray-900 text-white rounded-lg shadow-2xl px-1.5 py-1 flex items-center gap-0.5 border border-gray-700 animate-in fade-in-0 zoom-in-95 duration-200 ease-out">
        {/* Text formatting */}
        <BubbleMenuButton
          onClick={() => editor.chain().focus().toggleBold().run()}
          isActive={editor.isActive("bold")}
          icon={<Bold className="h-4 w-4" />}
          title="Bold"
          shortcut="Ctrl+B"
        />
        <BubbleMenuButton
          onClick={() => editor.chain().focus().toggleItalic().run()}
          isActive={editor.isActive("italic")}
          icon={<Italic className="h-4 w-4" />}
          title="Italic"
          shortcut="Ctrl+I"
        />
        <BubbleMenuButton
          onClick={() => editor.chain().focus().toggleStrike().run()}
          isActive={editor.isActive("strike")}
          icon={<Strikethrough className="h-4 w-4" />}
          title="Strikethrough"
        />

        {/* Divider */}
        <div className="w-px h-4 bg-gray-600 mx-0.5" />

        {/* Link */}
        <BubbleMenuButton
          onClick={handleLink}
          isActive={editor.isActive("link")}
          icon={<LinkIcon className="h-4 w-4" />}
          title="Link"
          shortcut="Ctrl+K"
        />

        {/* Divider */}
        <div className="w-px h-4 bg-gray-600 mx-0.5" />

        {/* Headings */}
        <BubbleMenuButton
          onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
          isActive={editor.isActive("heading", { level: 1 })}
          icon={<Heading1 className="h-4 w-4" />}
          title="Heading 1"
        />
        <BubbleMenuButton
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
          isActive={editor.isActive("heading", { level: 2 })}
          icon={<Heading2 className="h-4 w-4" />}
          title="Heading 2"
        />
        <BubbleMenuButton
          onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
          isActive={editor.isActive("heading", { level: 3 })}
          icon={<Heading3 className="h-4 w-4" />}
          title="Heading 3"
        />

        {/* Divider */}
        <div className="w-px h-4 bg-gray-600 mx-0.5" />

        {/* Code and Quote */}
        <BubbleMenuButton
          onClick={() => editor.chain().focus().toggleCode().run()}
          isActive={editor.isActive("code")}
          icon={<Code className="h-4 w-4" />}
          title="Inline Code"
        />
        <BubbleMenuButton
          onClick={() => editor.chain().focus().toggleBlockquote().run()}
          isActive={editor.isActive("blockquote")}
          icon={<Quote className="h-4 w-4" />}
          title="Quote"
        />
      </div>
    </BubbleMenu>
  );
}
